import { useSelector } from 'react-redux';

/**
 * The sidebar's nav groups, as the two-level sidebar sees them: which groups
 * this user gets, what the rail calls each one, and which routes make it the
 * active group. The rail itself renders `SidebarNav`'s own `NavItem`s (in
 * their `rail` variant); this is what selection and scrolling key off.
 *
 * The visibility rules and route tests mirror the `NavItem` groups rendered
 * by `SidebarNav` -- the rail is an index into that list, so a group shown
 * here must exist there (it is found by `data-nav-group={key}`), and a
 * route that highlights a group there should select the same group here.
 */

const test = (pattern) => (pathname) => new RegExp(pattern, 'i').test(pathname);

const NAV_GROUPS = [
  {
    key: 'deals',
    label: 'Deals',
    isActive: test('/deals'),
    isVisible: ({ isMember }) => isMember,
  },
  // Mailbox, Action Plan and Reports are standalone destinations, not
  // groups: one rail tile and one panel row each, with no children.
  {
    key: 'mailbox',
    label: 'Mailbox',
    isActive: test('/mailbox'),
    isVisible: ({ isMember }) => isMember,
  },
  {
    key: 'actionPlan',
    label: 'Action Plan',
    isActive: test('/action-plan'),
    isVisible: ({ isMember }) => isMember,
  },
  {
    key: 'reports',
    label: 'Reports',
    isActive: test('/reports'),
    isVisible: ({ isMember, hasReports }) => isMember && hasReports,
  },
  {
    // Includes the dev-only, admin-only Meeting Recorder; the group itself
    // is visible to every member.
    key: 'tools',
    label: 'Tools',
    isActive: test('/unlinked-items|/companies|/contacts|/m-record'),
    isVisible: ({ isMember }) => isMember,
  },
  {
    key: 'askAi',
    label: 'Ask AI',
    isActive: test('/ask-ai'),
    isVisible: ({ isMember, isAdmin }) => isMember && isAdmin,
  },
  {
    // "My Organization" in the panel; shortened so it fits under the icon on
    // one line.
    key: 'myOrg',
    label: 'My Org',
    isActive: (pathname) =>
      (test('/settings')(pathname) && !test('/customer/settings')(pathname)) ||
      test(
        '/pricing|/all-teams|/team-settings|/manage-members|/org-settings|/sales-org',
      )(pathname),
    isVisible: ({ isMember, isOrgAdmin, isDemoOrg }) =>
      isMember && isOrgAdmin && !isDemoOrg,
  },
  {
    key: 'setup',
    label: 'Setup',
    isActive: test(
      '/discovery-template|/integrations|/preferences|/profile|/deal-discovery',
    ),
    isVisible: ({ isMember }) => isMember,
  },
];

/** Each group's short label, as the rail shows it under the group's icon. */
export const RAIL_LABELS = Object.fromEntries(
  NAV_GROUPS.map((group) => [group.key, group.label]),
);

/** The groups this user's sidebar shows, in sidebar order. */
export const useNavGroups = () => {
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const { orgConfigs } = useSelector((state) => state.org.orgConfigs);
  const orgRole = userDetails?.orgRoles?.[0];
  const teamRole = userDetails?.teamRoles?.[0];

  const flags = {
    isMember:
      orgRole === 'ORG_ADMIN' ||
      teamRole === 'TEAM_ADMIN' ||
      teamRole === 'STANDARD',
    isAdmin: orgRole === 'ORG_ADMIN' || teamRole === 'TEAM_ADMIN',
    isOrgAdmin:
      orgRole === 'ORG_ADMIN' ||
      teamRole === 'TEAM_ADMIN' ||
      orgRole === 'WL_ORG_ADMIN',
    isDemoOrg: userDetails?.custom?.orgType === 'demo_org',
    hasReports: Boolean(orgConfigs?.generateWeeklyDealSummary),
  };

  return NAV_GROUPS.filter((group) => group.isVisible(flags));
};

/** The key of the group the current route belongs to, or null. */
export const activeGroupKey = (groups, pathname) =>
  groups.find((group) => group.isActive(pathname))?.key || null;
